import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { object, string } from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  emailAddress,
  emailAddressPlaceholder,
  emailAddressRequired,
  invalidEmailAddress,
  next,
  password,
  passwordPlaceHolder,
  passwordPolicyMessage,
  passwordRequired,
  registerHeader,
  registerSubHeader,
} from "../messages";
import {
  char8,
  lowerCase,
  number,
  passwordPolicy,
  passwordPolicyState,
  upperCase,
} from "../constants";
import { setRegisterData } from "../../redux/actions";
import Text from "../text/Text";
import { Input } from "../input/Input";
import InputErrorMessage from "../inputErrorMessage/InputErrorMessage";
import Button from "../button/Button";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registerDetails = useSelector((state) => state.common.registerDetails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeState, setActiveState] = useState(
    registerDetails?.password
      ? passwordPolicyState(registerDetails.password)
      : []
  );

  const initialValues = {
    email: registerDetails?.email || "",
    password: registerDetails?.password || "",
  };

  const validationSchema = object({
    email: string().email(invalidEmailAddress).required(emailAddressRequired),
    password: string()
      .matches(char8)
      .matches(upperCase)
      .matches(lowerCase)
      .matches(number)
      .required(passwordRequired),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (val, { resetForm }) => {
      setIsSubmitting(true);
      dispatch(setRegisterData({ ...registerDetails, ...val }));
      setTimeout(() => {
        setIsSubmitting(false);
        resetForm(initialValues);
        navigate("/register-step-two");
      }, 0);
    },
    enableReinitialize: true,
  });

  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const defaultErrorMessageProps = {
    touched,
    errors,
  };

  const disabled =
    registerDetails?.email === values?.email &&
    registerDetails?.password === values?.password
      ? !isValid || isSubmitting
      : !(isValid && dirty) || isSubmitting;

  return (
    <Box
      component="form"
      className="set-elements-vertically"
      onSubmit={handleSubmit}
    >
      <Box>
        <Text className="default-text" size={22} font="bold">
          {registerHeader}
        </Text>
        <Text className="grey-text mt-8" size={14}>
          {registerSubHeader}
        </Text>
        <Input
          customClass="mt-24"
          fullWidth
          showLabel
          label={emailAddress}
          placeholder={emailAddressPlaceholder}
          name="email"
          value={values.email}
          type="email"
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && Boolean(errors.email)}
        />
        <InputErrorMessage {...defaultErrorMessageProps} inputName="email" />

        <Input
          customClass="mt-16"
          fullWidth
          type="password"
          isPasswordVisible={true}
          showLabel
          label={password}
          placeholder={passwordPlaceHolder}
          name="password"
          value={values.password}
          onChange={(e) => {
            handleChange(e);
            setActiveState(passwordPolicyState(e.target.value));
          }}
          onBlur={handleBlur}
          error={touched.password && Boolean(errors.password)}
        />

        <Text className="grey-text mt-8" size={12}>
          {passwordPolicyMessage}
        </Text>
        <Box display="flex" gap={0.25}>
          {passwordPolicy.map((item, index) => {
            const lastIndex = passwordPolicy.length - 1;
            const styles = {
              color: activeState.includes(item) ? "#0CAD62" : "#FD371E",
            };
            return (
              <Box
                component="span"
                display="flex"
                justifyContent="center"
                alignItems="center"
                key={item}
                gap={0.25}
              >
                <Text size={12} style={styles} component="span">
                  {item}
                </Text>
                <CheckCircleIcon
                  sx={{ width: "12px", height: "12px", ...styles }}
                  className="info-popper-icon"
                />
                {index !== lastIndex && (
                  <Text size={12} className="grey-text" component="span">
                    |
                  </Text>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Button
        fullWidth
        variant="contained"
        label={next}
        type="submit"
        disabled={disabled}
      />
    </Box>
  );
};

export default Register;
